import * as React from "react"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@radix-ui/react-popover"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"

import { getAnnouncement } from "@/lib/api/announcement"
import { formatDateAndTime } from "@/lib/utils"
import { useMediaQuery } from "@/hooks/use-media-query"
import { Button, buttonVariants } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Credenza,
  CredenzaBody,
  CredenzaContent,
  CredenzaDescription,
  CredenzaHeader,
  CredenzaTitle,
  CredenzaTrigger,
} from "@/components/ui/credenza"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useSidebar } from "@/components/ui/sidebar"
import { DataTable } from "@/components/data-table"
import { Header } from "@/components/header"
import { Icons } from "@/components/icons"
import { Loader } from "@/components/loader"
import { announcementsColumns } from "@/components/table/columns/announcements-columns"
import { ZoneBookFilter } from "@/components/zone-book-filter"

export const Route = createFileRoute(
  "/_home/announcement/overall-announcement",
)({
  component: OverAllAnnouncementPage,
})
interface Announcement {
  subject: string
  created_at: string | Date
  header: string
  body: string
  created_by: string
}

function OverAllAnnouncementPage() {
  const isDesktop = useMediaQuery("(min-width: 1367px)")
  const isLaptop = useMediaQuery("(min-width: 768px)")
  const [defaultPage, setDefaultPage] = React.useState<number>(10)
  const [page, setPage] = React.useState<number>(1)
  const { open, toggleSidebar, state } = useSidebar()
  const [zones, setZones] = React.useState<number[]>([])
  const [books, setBooks] = React.useState<number[]>([])
  const [selectedAnnouncement, setSelectedAnnouncement] =
    React.useState<Announcement | null>(null)
  //console.log(selectedAnnouncement)
  const [nextPageUrl, setNextPageUrl] = React.useState<string | null>(null)
  const [prevPageUrl, setPrevPageUrl] = React.useState<string | null>(null)

  const { data: announcementList, isLoading } = useQuery({
    queryKey: ["announcement", defaultPage, page],
    queryFn: () => getAnnouncement(defaultPage, page),
  })

  React.useEffect(() => {
    if (announcementList) {
      setNextPageUrl(announcementList.next)
      setPrevPageUrl(announcementList.previous)
    }
  }, [announcementList])

  function getPageFromUrl(url: string): number {
    const params = new URLSearchParams(new URL(url).search)
    //console.log(url)

    return Number(params.get("page")) || 1
  }

  const totalCount = announcementList && announcementList?.count

  const pageCount =
    totalCount && defaultPage ? Math.ceil(totalCount / defaultPage) : 0

  const currentPage = page

  return (
    <main className="space-y-4 p-4">
      <div className="absolute">
        {!open && (
          <div className="flex h-48 items-center justify-center">
            <Loader />
          </div>
        )}

        <div
          className={`fixed ${open && (isDesktop || isLaptop) ? "w-[calc(100%-16rem)]" : "w-full"} top-0 flex flex-col gap-2 bg-white pt-5 dark:bg-[#221F33]`}
        >
          <Header
            subroutes={[
              {
                route: "/announcement",
                label: "Announcements",
              },
            ]}
          >
            Overall Announcements
          </Header>
          <div className="grid grid-cols-1 md:items-stretch">
          <div className="grid grid-cols-12">
            <div className="col-span-12 md:col-span-8">
              <div className="mb-4 grid grid-cols-1 gap-4 pt-1 md:grid-cols-12 md:items-center">
                <div className="flex flex-col md:col-span-4 md:flex-row md:items-center md:gap-4">
                  {/* Search Bar */}
                  <Input
                    placeholder="Search Announcement..."
                    className="w-full"
                  />
                </div>

                <div className="flex justify-center gap-2 md:col-span-8 md:justify-center">
                  {/* Show Rows */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <span className="flex cursor-pointer items-center rounded-lg border text-xs">
                        Show {defaultPage} rows{" "}
                        <Icons.down className="ml-2 size-4" />
                      </span>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      {[10, 25, 50, 100].map((pageSize) => (
                        <DropdownMenuItem
                          key={pageSize}
                          onSelect={() => {
                            setPage?.(1)
                            setDefaultPage?.(pageSize)
                          }}
                        >
                          Show {pageSize}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (prevPageUrl) {
                          const prevPage = getPageFromUrl(prevPageUrl)
                          setPage?.(prevPage)
                        }
                      }}
                      disabled={!prevPageUrl}
                    >
                      Previous
                    </Button>

                    {Array.from({ length: pageCount }, (_, i) => i)
                      .filter((_page) => {
                        return (
                          _page === 0 ||
                          _page === pageCount - 1 ||
                          (currentPage <= 1 && _page <= 2) ||
                          (currentPage >= pageCount - 2 &&
                            _page >= pageCount - 3) ||
                          (_page >= currentPage - 1 && _page <= currentPage + 1)
                        )
                      })
                      .map((__page, index, arr) => {
                        const prevPage = arr[index - 1]
                        const pageNumber = totalCount ? __page + 1 : __page
                        return (
                          <React.Fragment key={__page}>
                            {prevPage !== undefined &&
                              __page - prevPage > 1 && (
                                <span className="text-gray-400">...</span>
                              )}
                            <Button
                              key={__page}
                              variant={
                                pageNumber === currentPage
                                  ? "default"
                                  : "outline"
                              }
                              size="sm"
                              onClick={() => {
                                setPage?.(pageNumber)
                              }}
                            >
                              {totalCount ? pageNumber : pageNumber + 1}
                            </Button>
                          </React.Fragment>
                        )
                      })}

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (nextPageUrl) {
                          const nextPage = getPageFromUrl(nextPageUrl)
                          setPage?.(nextPage)
                        }
                      }}
                      disabled={!nextPageUrl}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Filter and Add New Announcement */}
            <div className="col-span-12 flex flex-row items-start justify-center gap-2 px-5 md:col-span-4 md:flex-row">
              <Popover>
                <PopoverTrigger className="rounded-lg border px-2 py-2">
                  <Icons.mixer />
                </PopoverTrigger>
                <PopoverContent className="w-52 pr-4">
                  <Card>
                    <CardHeader className="p-2">
                      <CardTitle>Filter</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="pt-4">
                        <ZoneBookFilter
                          setZones={setZones}
                          zones={zones}
                          setBooks={setBooks}
                          books={books}
                        />
                      </div>
                    </CardContent>
                  </Card>
                </PopoverContent>
              </Popover>
              <div>
                <Link
                  to="/announcement"
                  className={buttonVariants({ variant: "default" })}
                >
                  <Icons.send />
                  Sent New Notification
                </Link>
              </div>
            </div>
          </div>
          </div>
          
        </div>
      </div>
      {isLoading && (
        <div className="flex h-48 items-center justify-center">
          <Loader />
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 px-1 pt-24 sm:grid-cols-2 lg:grid-cols-4">
        {announcementList?.results.map((announcement: any, index: number) => {
          return (
            <Card key={index}>
              <div className="w-full overflow-hidden">
                <img
                  src="../card-image-sampler.png"
                  className="h-44 w-full object-cover"
                />
              </div>
              <CardHeader>{/* <CardTitle>Subject</CardTitle> */}</CardHeader>
              <CardContent>
                <div>
                  <h6 className="text-lg font-semibold">
                    {announcement.subject}
                  </h6>
                </div>
                <br />
                <div>
                  <span className="text-xs text-muted-foreground">
                    {`${announcement.created_by} | ${formatDateAndTime(announcement.created_at)}`}
                  </span>
                </div>
                <br />
                <div>
                  <span className="block whitespace-pre-wrap break-words text-justify">
                    {announcement.body}
                  </span>
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Credenza>
                  <CredenzaTrigger asChild>
                    <span
                      className="flex cursor-pointer px-0 text-primary underline"
                      onClick={() => {
                        setSelectedAnnouncement(announcement)
                      }}
                    >
                      Read more <Icons.doubleRight />
                    </span>
                  </CredenzaTrigger>
                  <CredenzaContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
                    <CredenzaHeader>
                      <CredenzaTitle className="text-2xl">
                        <div className="flex items-center justify-center gap-2">
                          <Icons.partyPopper className="text-primary" />
                          <Icons.partyPopper className="text-primary" />
                          Announcement
                          <Icons.partyPopper className="text-primary" />
                          <Icons.partyPopper className="text-primary" />
                        </div>
                      </CredenzaTitle>
                      <CredenzaDescription className="flex flex-col py-2">
                        <span className="text-xl font-medium">
                          {selectedAnnouncement?.subject}
                        </span>
                        <span className="text-sm font-bold">
                          {`${selectedAnnouncement?.created_by} | ${formatDateAndTime(selectedAnnouncement?.created_at ?? "")}`}
                        </span>
                        <br />
                        <span className="text-lg font-medium">
                          {selectedAnnouncement?.header}
                        </span>
                        <br />
                        <br />
                        {selectedAnnouncement?.body}
                      </CredenzaDescription>
                    </CredenzaHeader>
                  </CredenzaContent>
                </Credenza>
              </CardFooter>
            </Card>
          )
        })}
      </div>

      {/*<DataTable
        columns={announcementsColumns}
        data={announcementList?.results || []}
        inCard
      ></DataTable> */}
    </main>
  )
}
